// The constellations (02-DESIGN/03-console/03-the-signed-out-site.md § the
// visual): beside each position, a dozen points joined to their nearest
// neighbours, one of them in the teal, each section's its own and the same on
// every visit. Pure geometry in a 44-unit square; the page draws it as SVG,
// still, so the reduced-motion preference has nothing to still. Ported from
// the prototype the owner approved.

import { at, mulberry32 } from "./draw.ts";

export interface Constellation {
  readonly points: readonly { readonly x: number; readonly y: number; readonly r: number }[];
  readonly lines: readonly {
    readonly x1: number;
    readonly y1: number;
    readonly x2: number;
    readonly y2: number;
  }[];
  /** The point in the teal. */
  readonly hot: number;
}

/** The side of the square the constellation is drawn in. */
export const SIDE = 44;

/** The seeds of the four positions' constellations, in order. */
export const SEEDS: readonly number[] = [23, 41, 67, 79];

/** buildConstellation places `want` points apart and joins each to its three nearest; the same seed, the same mesh. */
export function buildConstellation(seed: number, want = 12): Constellation {
  const rand = mulberry32(seed);
  const apart = want > 8 ? 7 : 9;
  const pts: [number, number][] = [];
  for (let tries = 0; pts.length < want && tries < 2000; tries++) {
    const p: [number, number] = [4 + rand() * 36, 4 + rand() * 36];
    if (pts.every((q) => Math.hypot(p[0] - q[0], p[1] - q[1]) >= apart)) {
      pts.push(p);
    }
  }
  const seen = new Set<string>();
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  pts.forEach((a, i) => {
    const d = pts
      .map((b, j): [number, number] => [Math.hypot(a[0] - b[0], a[1] - b[1]), j])
      .filter(([, j]) => j !== i)
      .sort((x, y) => x[0] - y[0]);
    for (const [, j] of d.slice(0, want > 8 ? 3 : 2)) {
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const b = at(pts, j);
      lines.push({ x1: a[0], y1: a[1], x2: b[0], y2: b[1] });
    }
  });
  const hot = Math.floor(rand() * pts.length);
  const big = want > 8;
  const points = pts.map(([x, y], i) => ({
    x,
    y,
    r: big ? (i === hot ? 0.9 : 0.35 + rand() * 0.4) : i === hot ? 2.6 : 1.4 + rand() * 1.1,
  }));
  return { points, lines, hot };
}
